/**
 * `<wyatt-analytics data-event="...">` publishes a Wyatt analytics event from
 * the JSON in its `[data-payload]` script. See the "Analytics Events" section
 * of the README.
 *
 * - `page_context` and `view_item_list` publish when the element connects. A
 *   list rendered into a live region connects again on every rerender, which
 *   is a new set of impressions.
 * - Inside a dialog, they publish once per open of the dialog: now if it is
 *   open, and on each later open.
 * - `select_item` publishes on a click on a link inside it, with the product
 *   at its index in the closest `view_item_list`.
 * - `view_mini_cart` publishes once per open of its dialog, and never on
 *   connect. Its payload is read at that moment from the script
 *   `data-payload-from` selects inside the dialog, which the cart's live
 *   regions keep current.
 *
 * The mini cart has a `<wyatt-modal>` opener in each copy of the utility nav,
 * and every one of them opens the dialog on add to cart and fires its own
 * `modalOpen`, so one open arrives as several events. The element publishes
 * on the first and waits for the dialog to close before it publishes again.
 *
 * The element renders no box of its own (`display: contents`), so wrapping a
 * product card in it leaves grid and slider layouts unchanged.
 */
import { registerComponent } from "helpers";
import analytics, { parsePayload, selectItemPayload } from "analytics";

function safely(run) {
  try {
    run();
  } catch (e) {
    // Analytics must never throw into the page.
  }
}

function isOpen(dialog) {
  return dialog.open || dialog.getAttribute("active") === "true";
}

const DIALOG_OPEN_EVENTS = ["drawerOpen", "modalOpen"];
const DIALOG_CLOSE_EVENTS = ["close", "drawerClose", "modalClose"];

registerComponent(
  "wyatt-analytics",
  class WyattAnalytics extends HTMLElement {
    /** @type {object | null} */
    payload = null;

    /** @type {Element | null} */
    #dialog = null;

    #publishedThisOpen = false;

    #onClick = (event) => safely(() => this.#handleClick(event));

    #onDialogOpen = () => safely(() => this.#publishOnOpen());

    #onDialogClose = () => {
      this.#publishedThisOpen = false;
    };

    connectedCallback() {
      safely(() => {
        const { event } = this.dataset;

        this.payload = parsePayload(this.#ownPayloadScript());

        if (event === "select_item") {
          this.addEventListener("click", this.#onClick);
          return;
        }

        const dialog = this.closest("[data-dialog]");

        if (!dialog) {
          if (event !== "view_mini_cart") analytics.fromElement(this);
          return;
        }

        this.#watch(dialog);

        if (event !== "view_mini_cart" && isOpen(dialog)) {
          this.#publishOnOpen();
        }
      });
    }

    disconnectedCallback() {
      safely(() => {
        this.removeEventListener("click", this.#onClick);
        this.#unwatch();
      });
    }

    // Scoped to this element's own children, so a list never reads the
    // payload of a card nested inside it.
    #ownPayloadScript() {
      return Array.from(this.children).find((child) =>
        child.matches("[data-payload]"),
      );
    }

    #handleClick(event) {
      // A link that cancels its own navigation, such as a colour swatch that
      // swaps the card in place, is not a product selection.
      if (event.defaultPrevented) return;

      const link = event.target?.closest?.("a[href]");

      if (!link || !this.contains(link)) return;

      const list = this.parentElement?.closest(
        'wyatt-analytics[data-event="view_item_list"]',
      );
      const payload = selectItemPayload(list?.payload, this.payload?.index);

      if (payload) {
        analytics.publish(analytics.events.select_item, payload);
      }
    }

    #publishOnOpen() {
      if (this.#publishedThisOpen) return;

      this.#publishedThisOpen = true;

      const source = this.dataset.payloadFrom;

      this.payload = parsePayload(
        source ? this.#dialog?.querySelector(source) : this.#ownPayloadScript(),
      );

      analytics.fromElement(this);
    }

    #watch(dialog) {
      this.#unwatch();
      this.#dialog = dialog;

      DIALOG_OPEN_EVENTS.forEach((name) =>
        dialog.addEventListener(name, this.#onDialogOpen),
      );
      DIALOG_CLOSE_EVENTS.forEach((name) =>
        dialog.addEventListener(name, this.#onDialogClose),
      );
    }

    #unwatch() {
      if (!this.#dialog) return;

      DIALOG_OPEN_EVENTS.forEach((name) =>
        this.#dialog.removeEventListener(name, this.#onDialogOpen),
      );
      DIALOG_CLOSE_EVENTS.forEach((name) =>
        this.#dialog.removeEventListener(name, this.#onDialogClose),
      );

      this.#dialog = null;
      this.#publishedThisOpen = false;
    }
  },
);
